// src/pages/Login.jsx — the STAFF / ADMIN login door (/login).
// Role-gated: a student account (has students/{uid}) is rejected here and
// sent back to the student door. On success an access token is persisted.
import React, { useState } from "react";
import { signInWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { HiMail, HiLockClosed } from "react-icons/hi";
import { auth, db } from "../components/firebase/firebase";
import { friendlyAuthError } from "../lib/authErrors";
import AuthLayout from "../components/auth/AuthLayout";
import AuthField from "../components/auth/AuthField";
import { SEO } from "../components/common/SEO";

const PANEL_BULLETS = [
  "Create & manage MCQ tests",
  "Onboard students and track progress",
  "Publish question papers & resources",
];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const navigate = useNavigate();

  const validate = () => {
    const errs = {};
    if (!email.trim()) errs.email = "Email is required.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = "Enter a valid email address.";
    if (!password) errs.password = "Password is required.";
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setError("");
    if (!validate()) return;

    setLoading(true);
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);

      // Block students from entering the admin door.
      const studentSnap = await getDoc(doc(db, "students", user.uid));
      if (studentSnap.exists()) {
        await signOut(auth);
        setError("This login is for staff only. Please use the student login.");
        return;
      }

      const accessToken = await user.getIdToken();
      localStorage.setItem("accessToken", accessToken);
      navigate("/admin");
    } catch (err) {
      setError(friendlyAuthError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      accent="admin"
      panelTitle="Staff & tutor control center."
      panelSubtitle="Sign in to manage tests, students, and learning resources."
      bullets={PANEL_BULLETS}
    >
      <SEO title="Staff Login" description="Staff and tutor login for Ravi TestPapers." canonical="/login" noIndex />

      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-gray-800">Staff Login</h2>
        <p className="text-sm text-gray-400 mt-1">Sign in to your admin dashboard</p>
      </div>

      {error && (
        <div
          role="alert"
          className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600"
        >
          {error}
        </div>
      )}

      <form onSubmit={handleLogin} noValidate>
        <AuthField
          id="email"
          label="Email"
          type="email"
          icon={HiMail}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          autoComplete="email"
          required
          error={fieldErrors.email}
          accentRing="focus:ring-blue-300"
        />
        <AuthField
          id="password"
          label="Password"
          type="password"
          icon={HiLockClosed}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          autoComplete="current-password"
          required
          error={fieldErrors.password}
          accentRing="focus:ring-blue-300"
        />

        <button
          type="submit"
          disabled={loading}
          className="w-full mt-2 bg-slate-800 hover:bg-slate-700 disabled:bg-slate-400 text-white font-bold py-3 rounded-xl transition-colors shadow-lg shadow-slate-200 flex items-center justify-center gap-2"
        >
          {loading && (
            <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
          )}
          {loading ? "Signing in…" : "Sign In"}
        </button>
      </form>
    </AuthLayout>
  );
}
